import { questionPropertyMapper } from '@exam-management/common/utils';
import {
  type GetSingleQuestionPropertiesCountByLocationQuery,
  SingleQuestionPropertiesLocationType,
  useGetSingleQuestionPropertiesCountByLocationQuery,
} from '@exam-management/graphql';
import { useMemo } from 'react';

interface GetPropertiesCountProps {
  selectedSubtopics?: string[];
  selectedLOS?: string[];
  locationSelected: boolean;
}

export function useGetPropertiesCount(props: Readonly<GetPropertiesCountProps>) {
  const { selectedSubtopics, locationSelected: isLocationSelected, selectedLOS } = props;
  const isSelectedSubtopics = selectedSubtopics?.length !== 0;

  const { data: getSingleQuestionPropertiesResult, loading: isGettingQuestionPropertiesCount } =
    useGetSingleQuestionPropertiesCountByLocationQuery({
      variables: {
        input: {
          qBankStructureLevelItemIds: isSelectedSubtopics ? selectedSubtopics : undefined,
          losIds: !isSelectedSubtopics ? selectedLOS : undefined,
          type: isSelectedSubtopics
            ? SingleQuestionPropertiesLocationType.QbankStructureLevelItem
            : SingleQuestionPropertiesLocationType.LearningObjectiveStatement,
        },
      },
      skip: !isLocationSelected,
      fetchPolicy: 'network-only',
    });

  const singleQuestionProperties = useMemo(
    () => getSingleQuestionPropertiesResult?.qctGetSingleQuestionPropertiesCountByLocation,
    [getSingleQuestionPropertiesResult],
  );

  const propertyTagMapper = useMemo(() => {
    if (!singleQuestionProperties) {
      return [];
    }

    const tags = Array.from(new Set(flattenAndMapTags(singleQuestionProperties)));

    return tags;
  }, [singleQuestionProperties]);

  return {
    propertyTagMapper,
    isGettingQuestionPropertiesCount,
    singleQuestionProperties,
  };
}

function flattenAndMapTags(
  result: GetSingleQuestionPropertiesCountByLocationQuery['qctGetSingleQuestionPropertiesCountByLocation'],
) {
  return result.flatMap((result) =>
    result.tags.map((tag) => {
      const schemaName = questionPropertyMapper[tag.name as keyof typeof questionPropertyMapper];

      return {
        ...tag,
        schemaName,
      };
    }),
  );
}
